import { useSwipeable } from 'react-swipeable'
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useConfig, useWindowSize } from 'msp-integrations'
import { RecommendationCarouselProps } from './RecommendationCarousel.types'
import { twMerge } from 'tailwind-merge'
import {
  DefaultLeftIcon,
  DefaultRightIcon
} from './RecommendationCarousel.partials'
import * as S from './RecommendationCarousel.styles'

const iconStyles = {
  fill: '#000',
  stroke: '#000',
  strokeWidth: '10px',
  width: '24px'
}

const ArrowIcons = {
  left: DefaultLeftIcon,
  right: DefaultRightIcon
}

function getArrowIcon(
  cbAction: () => void,
  shouldDisplay = false,
  icon: keyof typeof ArrowIcons,
  fontWhite?: boolean
): React.ReactNode {
  const Icon = ArrowIcons[icon]
  const iconStyle = useMemo(
    () => ({
      fill: fontWhite ? '#ffffff' : iconStyles.fill,
      stroke: fontWhite ? '#ffffff' : iconStyles.stroke,
      strokeWidth: iconStyles.strokeWidth,
      width: iconStyles.width
    }),
    [fontWhite]
  )

  return (
    <div
      className={twMerge(
        S.ArrowIconWrapper,
        shouldDisplay ? '' : 'pointer-events-none opacity-0'
      )}
      onClick={cbAction}
    >
      <Icon {...iconStyle} />
    </div>
  )
}

function getIndicators(
  currentIndex: number,
  totalTiles: number,
  font: string,
  fontWhite: boolean
): JSX.Element {
  return (
    <div
      className={twMerge(
        font,
        S.IndicatorWrapper,
        fontWhite ? 'text-white' : ''
      )}
    >
      {currentIndex} / {totalTiles}
    </div>
  )
}

const RecommendationCarousel = ({
  hideArrows = false,
  hideControl = false,
  hideIndicators = true,
  productTiles = [],
  fontWhite = false,
  centerWhenSingle = false,
  carouselUse
}: RecommendationCarouselProps) => {
  const totalTiles = productTiles.length
  const {
    theme: { font }
  } = useConfig()
  const [tileDisplayIndex, setTileDisplayIndex] = useState<number>(1)
  const { width, breakpoints } = useWindowSize()
  const isMobile = width && width < breakpoints.lg
  const [showNextArrow, setShowNextArrow] = useState<boolean>(false)
  const [showPrevArrow, setShowPrevArrow] = useState<boolean>(false)
  const [tileWidth, setTileWidth] = useState(0)
  const [totalTilesWidth, setTotalTilesWidth] = useState(0)
  const [carouselWidth, setCarouselWidth] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [transformValue, setTransformValue] = useState<string>('')

  const calculateTransformNext = useCallback(() => {
    return isMobile || carouselUse === 'cartPromotionSlot'
      ? tileWidth * -tileDisplayIndex
      : carouselWidth - tileWidth * totalTiles
  }, [isMobile, tileWidth, tileDisplayIndex, carouselWidth, totalTiles])

  const calculateTransformPrev = useCallback(() => {
    return isMobile || carouselUse === 'cartPromotionSlot'
      ? tileWidth * -(tileDisplayIndex - 2)
      : 0
  }, [isMobile, tileWidth, tileDisplayIndex])

  const slideNext = useCallback(() => {
    if (tileDisplayIndex + 1 <= totalTiles) {
      setTileDisplayIndex(tileDisplayIndex + 1)
      setTransformValue(`translateX(${calculateTransformNext()}px)`)
    }
  }, [tileDisplayIndex, totalTiles, calculateTransformNext])

  const slidePrev = useCallback(() => {
    if (tileDisplayIndex - 1 > 0) {
      setTileDisplayIndex(tileDisplayIndex - 1)
      setTransformValue(`translateX(${calculateTransformPrev()}px)`)
    }
  }, [tileDisplayIndex, calculateTransformPrev])

  useEffect(() => {
    if (totalTilesWidth > carouselWidth || !hideArrows) {
      setShowNextArrow(true)
      setShowPrevArrow(true)
    }
  }, [totalTilesWidth, carouselWidth, hideArrows])

  useEffect(() => {
    const marginRightChildren = 14
    function handleResize() {
      if (
        carouselRef.current &&
        carouselRef.current.children[0] &&
        (carouselRef.current.children[0] as HTMLElement).children[1]
      ) {
        const tileWidth =
          (
            (carouselRef.current.children[0] as HTMLElement)
              .children[1] as HTMLElement
          ).offsetWidth + marginRightChildren
        setTileWidth(tileWidth)
        setTotalTilesWidth(tileWidth * totalTiles)
        setCarouselWidth(carouselRef.current.offsetWidth)
      } else {
        setTimeout(handleResize, 100)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [carouselRef, productTiles])

  const swipeHandlers = useSwipeable({
    onSwipedLeft: slideNext,
    onSwipedRight: slidePrev,
    trackMouse: false,
    preventScrollOnSwipe: true
  })

  return (
    <div ref={carouselRef} className={S.RecommendationCarouselWrapper}>
      <div
        {...swipeHandlers}
        className={twMerge(
          S.RecommendationCarouselTilesWrapper,
          centerWhenSingle && totalTiles === 1 ? 'justify-center' : ''
        )}
        style={{
          transform: transformValue
        }}
      >
        {totalTiles > 0 && productTiles.map((productTile) => productTile)}
      </div>
      {!hideControl && totalTiles > 1 && (
        <div
          className={twMerge(
            S.RecommendationCarouselControlsWrapper,
            fontWhite ? 'text-white' : ''
          )}
        >
          {getArrowIcon(slidePrev, showPrevArrow, 'left', fontWhite)}
          {(!hideIndicators || isMobile) &&
            getIndicators(tileDisplayIndex, totalTiles, font.medium, fontWhite)}
          {getArrowIcon(slideNext, showNextArrow, 'right', fontWhite)}
        </div>
      )}
    </div>
  )
}

export default RecommendationCarousel
